import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './components/InstructorOrPlayer.module.css'
import loginStyles from './components/LoginMethod.module.css'
import baseLoginStyles from './Login.module.css'
import { SingleValue } from 'react-select'
import { Select, SelectOption } from '../../common/components/Select/Select'
import { LANGUAGE_SELECT_DEFAULT_OPTION, LANGUAGE_SELECT_OPTIONS } from '../../common/constants'
import classNames from 'classnames'
import { useFadeAnimation } from '../../hooks/FadeAnimation/useFadeAnimation'
import { useTheme } from '../../contexts/ThemeContext'
import seppoBkgImage from '../../assets/images/seppo_login_bkg.jpg'
import { LoginCard } from './components/LoginCard'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { routes } from '../../routes'
import { Form } from 'react-final-form'
import { PasswordResetFormValues } from './components/PasswordResetForm'
import { changePlayerAccountPasswordRequest, getPlayerAccountPasswordTokenRequest } from '../../api/userApiService'
import { useNotification } from '../../contexts/NotificationContext'
import { FormErrorType } from '../../types/commonTypes'
import { InputFormField } from '../../common/components/Form/InputFormField/InputFormField'
import { Button } from '../../common/components/button/Button'
import { safeIsNullOrEmpty } from '../../util/string'
import { HelpMenu } from '../../composites/HelpMenu/HelpMenu'

const validEmail =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

enum PasswordResetStep {
  ORDER = 'ORDER',
  SET_PASSWORD = 'SET_PASSWORD',
}

export const PasswordResetPlayerAccount: React.FC = () => {
  const { i18n, t } = useTranslation()
  const { fadeTransition } = useFadeAnimation()
  const navigate = useNavigate()
  const { notifySuccess, notifyError } = useNotification()
  const { logoUrl, backgroundImageUrl, hasCustomColors, loginLanguage, loginBottomLogoUrl } = useTheme()
  const queryParams = useMemo(() => new URLSearchParams(window.location.search), [])
  const { token } = useParams()

  const [searchParams] = useSearchParams()
  const tokenParam = searchParams.get('token')

  const [step] = useState<PasswordResetStep>(
    safeIsNullOrEmpty(token) && safeIsNullOrEmpty(tokenParam)
      ? PasswordResetStep.ORDER
      : PasswordResetStep.SET_PASSWORD,
  )

  const [defaultLanguageOption] = useState(() => {
    return LANGUAGE_SELECT_OPTIONS.find((option) => option.value === i18n.language) ?? LANGUAGE_SELECT_DEFAULT_OPTION
  })

  const [currLanguageOption, setCurrLanguageOption] = useState(defaultLanguageOption)

  useEffect(() => {
    setCurrLanguageOption(defaultLanguageOption)
  }, [defaultLanguageOption])

  const [themeBackgroundStyle, setThemeBackgroundStyle] = useState<React.CSSProperties>(
    backgroundImageUrl
      ? { backgroundImage: `url(${backgroundImageUrl})`, backgroundSize: 'cover' }
      : window.innerWidth > 500 && !hasCustomColors()
      ? { backgroundImage: `url(${seppoBkgImage})`, backgroundSize: 'cover' }
      : {},
  )

  const handleChangeLanguage = (selectOption: SingleValue<SelectOption>) => {
    i18n.changeLanguage(selectOption?.value)
    setCurrLanguageOption(selectOption || defaultLanguageOption)
  }

  useEffect(() => {
    const language = queryParams.get('l')
    if (language) i18n.changeLanguage(language)
  }, [i18n, queryParams])

  useEffect(() => {
    if (backgroundImageUrl)
      setThemeBackgroundStyle(
        backgroundImageUrl
          ? {
              background: `linear-gradient(180deg, rgba(255, 255, 255, 0.70) 0%, rgba(255, 255, 255, 0.00) 100%), url(${backgroundImageUrl}) lightgray 50%`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
            }
          : {},
      )
    else if (window.innerWidth > 500 && !hasCustomColors())
      setThemeBackgroundStyle({ backgroundImage: `url(${seppoBkgImage})`, backgroundSize: 'cover' })
    else setThemeBackgroundStyle({ backgroundColor: 'white' })
  }, [backgroundImageUrl, hasCustomColors])

  const onOrderLink = useCallback(
    async (values: PasswordResetFormValues) => {
      if (!values.email) return
      const result = await getPlayerAccountPasswordTokenRequest({ email: values.email })
      if (result.success) {
        notifySuccess({
          title: t('login.password_reset_link_order_success.title', 'Password change link sent'),
          content: t(
            'login.password_reset_link_order_success.content',
            'If a user account exists with the given email address, you should receive a link to reset password within a few minutes.',
          ),
        })
        navigate(routes.login)
      } else {
        notifyError({
          title: t('login.password_reset_link_order_failed.title', 'Password link order failed'),
          content: t(
            'login.password_reset_link_order_failed.content',
            'Please try again later or contact us for support.',
          ),
        })
      }
    },
    [notifySuccess, t, navigate, notifyError],
  )

  const onSetPassword = useCallback(
    async (values: PasswordResetFormValues) => {
      if (!tokenParam || !values.password || !values.passwordConfirmation) return
      if (values.password.trim() !== values.passwordConfirmation.trim()) {
        notifyError({
          title: t('login.password_change_error.title', 'Password change failed'),
          content: t('login.password_change_error.no_match', 'Passwords don`t match.'),
        })
        return
      }
      const result = await changePlayerAccountPasswordRequest({
        token: tokenParam || '',
        newPassword: values.password,
      })
      if (result.success) {
        if (result.value.success) {
          notifySuccess({
            title: t('login.password_change_success.title', 'Password changed'),
            content: result.value.msg ?? '',
          })
          navigate(routes.login)
        } else if (result.value.msg === 'no_token') {
          notifyError({
            title: t('login.password_change_error.title', 'Password change failed'),
            content: t('login.password_change_error.no_token_error', 'Password change token not accepted'),
          })
        } else {
          notifyError({
            title: t('login.password_change_error.title', 'Password change failed'),
            content: result.value.msg ?? '',
          })
        }
      } else {
        notifyError({
          title: t('login.password_change_serror.title', 'Password change failed'),
          content: t('login.password_change_error.content', 'Please try again later or contact us for support.'),
        })
      }
    },
    [tokenParam, notifyError, t, notifySuccess, navigate],
  )

  const validate = useCallback(
    (values: Partial<PasswordResetFormValues>): FormErrorType<PasswordResetFormValues> => {
      if (step === PasswordResetStep.ORDER) {
        return validEmail.test(values.email + '')
          ? {}
          : { email: t('validation_errors.invalid_email', 'Invalid email') }
      } else {
        const err: FormErrorType<PasswordResetFormValues> = {}
        return {
          ...err,
          ...(safeIsNullOrEmpty(values.password) ? { password: t('validation_errors.required', 'Required') } : {}),
          ...(safeIsNullOrEmpty(values.passwordConfirmation)
            ? { passwordConfirmation: t('validation_errors.required', 'Required') }
            : {}),
        }
      }
    },
    [step, t],
  )

  return (
    <div className={baseLoginStyles.pageContainer}>
      <div className={baseLoginStyles.languageContainer}>
        <Select
          options={LANGUAGE_SELECT_OPTIONS}
          defaultValue={
            loginLanguage
              ? LANGUAGE_SELECT_OPTIONS.find((option) => option.value === loginLanguage) ?? defaultLanguageOption
              : defaultLanguageOption
          }
          value={currLanguageOption}
          onChange={handleChangeLanguage}
          className={baseLoginStyles.languageSelect}
        />
      </div>
      <div className={classNames(styles.backgroundContainer, fadeTransition)} style={themeBackgroundStyle}>
        <img
          /*onClick={handleResetTheme}*/
          src={logoUrl}
          alt='Seppo'
          height={logoUrl.includes('Seppo_logo') ? 26 : undefined}
          className={styles.seppoLogo}
        />
        <div className={classNames(styles.cardsContainer)}>
          <LoginCard
            big
            removeMinHeight
            onBack={() => navigate(routes.login)}
            iconLabel={t('common.back')}
            icon={'arrowLeft'}
            title={t('login.instructor.password_change.order_form.title', 'Reset your password')}
            variant={'oneCard'}
            onBackDisabled={false}
          >
            <div className={styles.layoutAlignmentWrapper}>
              <Form<PasswordResetFormValues>
                onSubmit={step === PasswordResetStep.ORDER ? onOrderLink : onSetPassword}
                validate={validate}
              >
                {({ handleSubmit, submitting, submitError, modifiedSinceLastSubmit }) => (
                  <form onSubmit={handleSubmit} className={styles.loginWithGameCode}>
                    <div className={styles.enterCodeColumn}>
                      {step === PasswordResetStep.ORDER ? (
                        <>
                          <InputFormField
                            label='Email'
                            placeholder={t(
                              'login.instructor.password_change.order.email_placeholder',
                              'Enter your email address',
                            )}
                            srOnlyLabel
                            name='email'
                            type='email'
                            big
                          />
                        </>
                      ) : (
                        <>
                          <p>
                            {t(
                              'login.instructor.password_change.set_password_form.body',
                              'Enter your new password. The password must be at least 10 characters long with both upper and lower case letters between a to z and numbers',
                            )}
                          </p>
                          <div className={loginStyles.passwordResetFieldsContainer}>
                            <InputFormField
                              label='Password'
                              placeholder={t(
                                'login.instructor.password_change.order.password_placeholder',
                                'Your new password',
                              )}
                              srOnlyLabel
                              name='password'
                              type='password'
                              big
                            />
                            <InputFormField
                              label='Confirm Password'
                              placeholder={t(
                                'login.instructor.password_change.order.password_confirm_placeholder',
                                'Confirm new password',
                              )}
                              srOnlyLabel
                              name='passwordConfirmation'
                              type='password'
                              big
                            />
                          </div>
                        </>
                      )}
                      <div className={styles.enterCodeRow} style={{ justifyContent: 'center' }}>
                        <Button type='submit' style={{ minWidth: '200px' }}>
                          {submitting
                            ? t('loading', 'Loading...')
                            : step === PasswordResetStep.ORDER
                            ? t('login.instructor.password_change.order_link_button', 'Order reset link')
                            : t('login.instructor.password_change.set_password_button', 'Set password')}
                        </Button>
                      </div>
                    </div>
                  </form>
                )}
              </Form>
            </div>
          </LoginCard>
        </div>
        <div className={styles.bottomCodeArea}>
          <img className={styles.bottomLogo} alt='' src={loginBottomLogoUrl} />
        </div>
      </div>
      <p className={baseLoginStyles.learnMoreBottomOffset}>
        {t('login.new_to_seppo', 'New to Seppo?')}{' '}
        <a data-testid={'LEARN_MORE_LINK'} rel='noreferrer' href='https://seppo.io/'>
          {t('login.learn_more', 'Learn more here')}
        </a>
      </p>
      <div className={baseLoginStyles.hideOnSmallScreen}>
        <HelpMenu
          buttonClassName={classNames(baseLoginStyles.invertedHelpButton, baseLoginStyles.hideOnSmallScreen)}
          shouldMirror={false}
        />
      </div>
    </div>
  )
}
